'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, Plus, ChevronDown, User, LayoutDashboard, LogOut, MapPin, Search, ShoppingBag, MessageCircle } from 'lucide-react';

import NotificationCenter from '@/components/NotificationCenter';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { getMainCategories, CategoryRow } from '@/lib/services/categoriesService';

// Fallback static nav (used if DB fetch fails or returns empty)
const FALLBACK_NAV = [
  { label: 'Buy/Sell', href: '/listings-page' },
  { label: 'Property', href: '/listings-page?cat=properties' },
  { label: 'Vehicles', href: '/listings-page?cat=vehicles' },
  { label: 'Jobs', href: '/listings-page?cat=jobs' },
  { label: 'Services', href: '/listings-page?cat=services' },
  { label: 'Recharge', href: '/listings-page?cat=recharge' },
  { label: 'Travel', href: '/listings-page?cat=travel' },
];

const FALLBACK_MORE = [
  { label: 'Mobiles', href: '/listings-page?cat=mobiles' },
  { label: 'PG / Hostel', href: '/listings-page?cat=pg' },
  { label: 'Agriculture', href: '/listings-page?cat=agriculture' },
  { label: 'Furniture', href: '/listings-page?cat=furniture' },
  { label: 'Electronics', href: '/listings-page?cat=electronics' },
];

const NAV_LIMIT = 7;

interface NavItem {
  label: string;
  href: string;
}

function categoryToNavItem(cat: CategoryRow): NavItem {
  return {
    label: cat.name,
    href: `/listings-page?cat=${cat.slug}`,
  };
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [navLinks, setNavLinks] = useState<NavItem[]>(FALLBACK_NAV);
  const [moreLinks, setMoreLinks] = useState<NavItem[]>(FALLBACK_MORE);
  const pathname = usePathname();
  const { user, signOut } = useAuth();
  const { unreadCount: msgUnread } = useNotifications();

  const isHomepage = pathname === '/';

  useEffect(() => {
    getMainCategories(20).then((cats) => {
      if (!cats || cats.length === 0) return;
      const items = cats.map(categoryToNavItem);
      setNavLinks(items.slice(0, NAV_LIMIT));
      setMoreLinks(items.slice(NAV_LIMIT));
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
    setMoreOpen(false);
  }, [pathname]);

  if (isHomepage) return null;

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/98 backdrop-blur-xl shadow-nav'
            : 'bg-white border-b border-[#E2E2E7]'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-[62px]">

            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0 group">
              <div className="w-8 h-8 rounded-[10px] bg-[#D93025] flex items-center justify-center shadow-sm group-hover:shadow-red transition-shadow duration-200">
                <MapPin size={15} className="text-white" strokeWidth={2.5} />
              </div>
              <span className="font-sans text-[17px] font-700 text-[#1D1D1F] tracking-tight leading-none">
                Namma<span className="text-[#D93025]">Price</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href || (link?.href !== '/' && pathname?.startsWith(link?.href?.split('?')?.[0]));
                return (
                  <Link
                    key={`nav-${link?.label}`}
                    href={link?.href}
                    className={`px-3.5 py-2 text-[13px] font-500 rounded-[8px] transition-all duration-150 ${
                      isActive
                        ? 'text-[#D93025] bg-[#FFEBEE] font-600'
                        : 'text-[#3D3D3F] hover:text-[#D93025] hover:bg-[#FFEBEE]/60'
                    }`}
                  >
                    {link?.label}
                  </Link>
                );
              })}

              {/* More dropdown — only shown if there are extra categories */}
              {moreLinks.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setMoreOpen(!moreOpen)}
                    className="flex items-center gap-1 px-3.5 py-2 text-[13px] font-500 rounded-[8px] text-[#3D3D3F] hover:text-[#D93025] hover:bg-[#FFEBEE]/60 transition-all duration-150"
                  >
                    More
                    <ChevronDown size={12} className={`transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {moreOpen && (
                    <div className="absolute left-0 top-full mt-2 w-52 bg-white border border-[#E2E2E7] rounded-[14px] shadow-modal z-50 py-2 animate-fade-in overflow-hidden">
                      {moreLinks?.map((link) => (
                        <Link
                          key={link?.label}
                          href={link?.href}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-[#3D3D3F] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors"
                          onClick={() => setMoreOpen(false)}
                        >
                          {link?.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Right Actions */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Search icon */}
              <Link
                href="/listings-page"
                className="w-9 h-9 rounded-[10px] border border-[#E2E2E7] flex items-center justify-center text-[#6E6E73] hover:text-[#D93025] hover:border-[#D93025] transition-all duration-150"
                aria-label="Search"
              >
                <Search size={15} />
              </Link>

              {/* Messages icon */}
              {user && (
                <Link
                  href="/messages"
                  className="relative w-9 h-9 rounded-[10px] border border-[#E2E2E7] flex items-center justify-center text-[#6E6E73] hover:text-[#D93025] transition-colors touch-manipulation"
                  aria-label="Messages"
                >
                  <MessageCircle size={16} />
                  {msgUnread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-[#D93025] border border-white text-white text-[9px] font-700 flex items-center justify-center px-0.5">
                      {msgUnread > 9 ? '9+' : msgUnread}
                    </span>
                  )}
                </Link>
              )}

              {/* Notification Bell */}
              {user && <NotificationCenter />}

              <Link
                href="/post-listing"
                className="flex items-center gap-1.5 bg-[#D93025] text-white px-4 py-2 rounded-[10px] text-[13px] font-600 hover:bg-[#B71C1C] transition-all duration-150 shadow-sm hover:shadow-red active:scale-95"
              >
                <Plus size={14} strokeWidth={2.5} />
                Post Free Ad
              </Link>

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 px-3 py-2 border border-[#E2E2E7] rounded-[10px] text-[13px] font-500 text-[#3D3D3F] hover:bg-[#F5F5F7] hover:border-[#D1D1D6] transition-all duration-150"
                >
                  <div className="w-6 h-6 rounded-full bg-[#FFEBEE] flex items-center justify-center">
                    <User size={12} className="text-[#D93025]" />
                  </div>
                  <span>Account</span>
                  <ChevronDown size={12} className={`text-[#A1A1A6] transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`} />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#E2E2E7] rounded-[14px] shadow-modal z-50 py-2 animate-fade-in overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#F0F0F5]">
                      <p className="text-[12px] text-[#A1A1A6] font-500">Signed in as</p>
                      <p className="text-[13px] font-600 text-[#1D1D1F] mt-0.5">{user?.email || 'Guest User'}</p>
                    </div>
                    <Link href="/sign-up-login-screen" className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-[#3D3D3F] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors" onClick={() => setProfileOpen(false)}>
                      <User size={14} className="text-[#A1A1A6]" />
                      Login / Sign Up
                    </Link>
                    <Link href="/seller-dashboard" className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-[#3D3D3F] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors" onClick={() => setProfileOpen(false)}>
                      <LayoutDashboard size={14} className="text-[#A1A1A6]" />
                      Seller Dashboard
                    </Link>
                    <Link href="/buyer-dashboard" className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-[#3D3D3F] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors" onClick={() => setProfileOpen(false)}>
                      <ShoppingBag size={14} className="text-[#A1A1A6]" />
                      Buyer Dashboard
                    </Link>
                    <Link href="/admin-dashboard" className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-[#3D3D3F] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors" onClick={() => setProfileOpen(false)}>
                      <LayoutDashboard size={14} className="text-[#A1A1A6]" />
                      Admin Dashboard
                    </Link>
                    <div className="border-t border-[#F0F0F5] my-1" />
                    <button
                      onClick={async () => { await signOut(); setProfileOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-[#A1A1A6] hover:bg-[#F5F5F7] transition-colors"
                    >
                      <LogOut size={14} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile: Post Ad + Hamburger */}
            <div className="lg:hidden flex items-center gap-1.5">
              {user && (
                <Link
                  href="/messages"
                  className="relative w-9 h-9 rounded-[10px] border border-[#E2E2E7] flex items-center justify-center text-[#6E6E73] hover:text-[#D93025] transition-colors touch-manipulation"
                  aria-label="Messages"
                >
                  <MessageCircle size={16} />
                  {msgUnread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-[#D93025] border border-white text-white text-[9px] font-700 flex items-center justify-center px-0.5">
                      {msgUnread > 9 ? '9+' : msgUnread}
                    </span>
                  )}
                </Link>
              )}
              {user && <NotificationCenter />}
              <Link
                href="/post-listing"
                className="flex items-center gap-1 bg-[#D93025] text-white px-3 py-1.5 rounded-[8px] text-[12px] font-600 shadow-sm active:scale-95 transition-transform touch-manipulation"
              >
                <Plus size={12} strokeWidth={2.5} />
                Post Ad
              </Link>
              <button
                className="w-9 h-9 rounded-[10px] border border-[#E2E2E7] flex items-center justify-center hover:bg-[#F5F5F7] transition-colors touch-manipulation"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-[#F0F0F5] px-4 py-3 space-y-0.5 animate-slide-down shadow-modal">
            {[...navLinks, ...moreLinks]?.map((link) => {
              const isActive = pathname === link?.href || (link?.href !== '/' && pathname?.startsWith(link?.href?.split('?')?.[0]));
              return (
                <Link
                  key={`mobile-nav-${link?.label}`}
                  href={link?.href}
                  className={`flex items-center gap-3 px-3 py-2.5 text-[13px] font-500 rounded-[10px] transition-colors ${
                    isActive ? 'text-[#D93025] bg-[#FFEBEE] font-600' : 'text-[#3D3D3F] hover:bg-[#F5F5F7]'
                  }`}
                  onClick={() => setMobileOpen(false)}
                >
                  {link?.label}
                </Link>
              );
            })}
            <div className="pt-2 border-t border-[#F0F0F5] space-y-1">
              <Link
                href="/seller-dashboard"
                className="flex items-center gap-3 px-3 py-2.5 text-[13px] font-500 text-[#3D3D3F] hover:bg-[#F5F5F7] rounded-[10px]"
                onClick={() => setMobileOpen(false)}
              >
                <LayoutDashboard size={15} className="text-[#A1A1A6]" />
                Seller Dashboard
              </Link>
              <Link
                href="/buyer-dashboard"
                className="flex items-center gap-3 px-3 py-2.5 text-[13px] font-500 text-[#3D3D3F] hover:bg-[#F5F5F7] rounded-[10px]"
                onClick={() => setMobileOpen(false)}
              >
                <ShoppingBag size={15} className="text-[#A1A1A6]" />
                Buyer Dashboard
              </Link>
              <Link
                href="/admin-dashboard"
                className="flex items-center gap-3 px-3 py-2.5 text-[13px] font-500 text-[#3D3D3F] hover:bg-[#F5F5F7] rounded-[10px]"
                onClick={() => setMobileOpen(false)}
              >
                <LayoutDashboard size={15} className="text-[#A1A1A6]" />
                Admin Dashboard
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="flex items-center justify-center gap-2 border border-[#E2E2E7] rounded-[10px] py-2.5 text-[13px] font-500 text-[#3D3D3F] hover:bg-[#F5F5F7]"
                onClick={() => setMobileOpen(false)}
              >
                <User size={14} />
                Login / Sign Up
              </Link>
            </div>
          </div>
        )}
      </header>

      {profileOpen && <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />}
      {moreOpen && <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />}
    </>
  );
}